/**
 * Вспомогательные функции для работы с датами
 */

/**
 * Получение массива всех дат между начальной и конечной датой включительно
 * @param {Date} startDate - Начальная дата
 * @param {Date} endDate - Конечная дата
 * @returns {Array<Date>} Массив дат
 */
export const getDatesBetween = (startDate, endDate) => {
    const dates = [];
    const currentDate = new Date(startDate);
    const lastDate = new Date(endDate);
    
    // Убедимся, что даты в правильном порядке
    if (currentDate > lastDate) {
      return getDatesBetween(endDate, startDate);
    }
    
    // Добавляем по одному дню, пока не достигнем конечной даты
    while (currentDate <= lastDate) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates;
  };
  
  /**
   * Расчет количества рабочих дней между двумя датами с учетом выходных и праздников
   * 
   * @param {Date} startDate - Начальная дата
   * @param {Date} endDate - Конечная дата
   * @param {Array} holidays - Массив праздничных дней (опционально)
   * @returns {number} Количество рабочих дней
   */
  export const getWorkingDaysBetween = (startDate, endDate, holidays = []) => {
    // Получаем все даты между начальной и конечной
    const allDates = getDatesBetween(startDate, endDate);
    
    // Преобразуем все праздники в строковый формат для сравнения
    const holidayStrings = holidays.map(h => h.date);
    
    // Фильтруем только рабочие дни (пн-пт, не являющиеся праздниками)
    const workingDays = allDates.filter(date => {
      const dayOfWeek = date.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // 0 = вс, 6 = сб
      const dateStr = date.toISOString().split('T')[0]; // формат YYYY-MM-DD
      const isHoliday = holidayStrings.includes(dateStr);
      
      return !isWeekend && !isHoliday;
    });
    
    return workingDays.length;
  };
  
  /**
   * Проверка, является ли дата рабочим днем
   * 
   * @param {Date} date - Проверяемая дата
   * @param {Array} holidays - Массив праздничных дней (опционально)
   * @returns {boolean} true, если дата является рабочим днем
   */
  export const isWorkingDay = (date, holidays = []) => {
    const dayOfWeek = date.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // 0 = вс, 6 = сб
    
    if (isWeekend) return false;
    
    // Проверяем, является ли день праздником
    const dateStr = date.toISOString().split('T')[0];
    const isHoliday = holidays.some(h => h.date === dateStr && h.type === 'holiday');
    
    return !isHoliday;
  };
  
  /**
   * Добавление рабочих дней к дате
   * 
   * @param {Date} date - Исходная дата
   * @param {number} days - Количество рабочих дней для добавления
   * @param {Array} holidays - Массив праздничных дней (опционально)
   * @returns {Date} Новая дата
   */
  export const addWorkingDays = (date, days, holidays = []) => {
    // Преобразуем все праздники в строковый формат для сравнения
    const holidayStrings = holidays.map(h => h.date);
    
    let result = new Date(date);
    let remainingDays = days;
    
    while (remainingDays > 0) {
      result.setDate(result.getDate() + 1);
      
      // Проверяем, что день не выходной и не праздник
      const dayOfWeek = result.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // 0 = вс, 6 = сб
      const dateStr = result.toISOString().split('T')[0];
      const isHoliday = holidayStrings.includes(dateStr);
      
      if (!isWeekend && !isHoliday) {
        remainingDays--;
      }
    }
    
    return result;
  };
  
  /**
   * Форматирование даты в локализованную строку
   * 
   * @param {Date} date - Дата для форматирования
   * @param {string} format - Формат (short, medium, long)
   * @returns {string} Отформатированная дата
   */
  export const formatDate = (date, format = 'medium') => {
    if (!date) return '';
    
    const options = {
      short: { day: '2-digit', month: '2-digit', year: 'numeric' },
      medium: { day: 'numeric', month: 'long', year: 'numeric' },
      long: { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }
    };
    
    return date.toLocaleDateString('ru-RU', options[format] || options.medium);
  };
  
  export default {
    getDatesBetween,
    getWorkingDaysBetween,
    isWorkingDay,
    addWorkingDays,
    formatDate
  };